<template>
    <OnBoardingForm :title="$t('Risk Profile - On Boarding')" :current-step="2">

        <div class="max-w-7xl mx-auto sm:px-6 lg:px-8">
            <form class="flex flex-col space-y-6" @submit.prevent="submitForm">
                <FormSection>
                    <template #title>
                        {{ $t('Questions about risk capacity') }}
                    </template>
                    <template #description>
                        {{ $t('The following questions are designed to determine your risk capacity as accurately as possible.') }}
                    </template>

                    <div class="flex flex-col space-y-8">
                        <div v-for="(question, idx) in questions.risk_capacity">
                            <Label :value="$t(question.question)" class="mb-3"/>
                            <SimpleRadioGroupOptions :options="question.options"
                                                     containerClasses="flex flex-wrap space-x-3"
                                                     v-model="form.risk_capacity[idx]"></SimpleRadioGroupOptions>
                            <JetInputError class="mt-3"
                                           :message="form.errors[`risk_capacity.${idx}.name`]"></JetInputError>
                        </div>
                    </div>
                </FormSection>

                <FormSection>
                    <template #title>
                        {{ $t('Questions about risk appetite') }}
                    </template>
                    <template #description>
                        {{ $t('The following questions are designed to determine your risk appetite as accurately as possible.') }}
                    </template>

                    <div class="flex flex-col space-y-8">
                        <div v-for="(question, idx) in questions.risk_tolerance">
                            <Label :value="$t(question.question)" class="mb-3"/>
                            <RadioGroupOptions :options="question.options"
                                               containerClasses="space-y-3"
                                               v-model="form.risk_tolerance[idx]"></RadioGroupOptions>
                            <JetInputError class="mt-3"
                                           :message="form.errors[`risk_tolerance.${idx}.name`]"></JetInputError>
                        </div>
                    </div>
                </FormSection>

                <div class="bg-white shadow overflow-hidden sm:rounded-lg border-2 border-primary-500" v-if="questionsAnswered">
                    <div class="px-4 py-5 sm:px-6 border-b border-gray-200">
                        <h3 class="text-xl leading-6 font-medium text-primary-700">{{ $t('Result and risk classification') }}</h3>
                        <p class="mt-1 max-w-2xl text-sm text-gray-500">{{ $t('Based on the answers you have given, the following risk profile has been determined') }}</p>
                    </div>
                    <div class="px-4 py-5 sm:px-6 flex items-center">
                        <div class="w-1/3 text-gray-600 font-bold">
                            {{ $t(results[totalRiskScore].titleKey) }}
                        </div>
                        <div>
                            {{ $t(results[totalRiskScore].descriptionKey) }}
                        </div>
                    </div>
                </div>

                <FormSection>
                    <template #title>
                        {{ $t('Additional information on knowledge and experience (appropriateness)') }}
                    </template>
                    <template #description>

                    </template>

                    <div class="flex flex-col space-y-8">
                        <div v-for="(question, idx) in questions.experience">
                            <Label :value="$t(question.question)" class="mb-3"/>
                            <SimpleRadioGroupOptions :options="question.options"
                                                     containerClasses="flex flex-wrap space-x-3"
                                                     v-model="form.experience[idx]"></SimpleRadioGroupOptions>
                            <JetInputError class="mt-3"
                                           :message="form.errors[`experience.${idx}.name`]"></JetInputError>
                        </div>
                    </div>
                </FormSection>

                <div class="flex justify-end">
                    <JetActionMessage :on="form.recentlySuccessful" class="mr-3">
                        {{ $t('Saved.') }}
                    </JetActionMessage>

                    <JetButton :class="{ 'opacity-25': form.processing }" :disabled="form.processing">
                        {{ $t('Save & continue') }}
                    </JetButton>
                </div>
            </form>
        </div>

    </OnBoardingForm>
</template>

<script>
import OnBoardingForm from '@/Layouts/OnBoardingForm.vue';
import FormSection from "@/Components/Form/FormSection";
import Label from '@/Components/Form/Label';
import JetButton from "@/Jetstream/Button";
import JetActionMessage from "@/Jetstream/ActionMessage";
import JetInputError from "@/Jetstream/InputError";
import {defineComponent} from "vue";
import SimpleRadioGroupOptions from "../../Components/Form/SimpleRadioGroupOptions";
import RadioGroupOptions from "../../Components/Form/RadioGroupOptions";

export default defineComponent({
    components: {
        SimpleRadioGroupOptions,
        RadioGroupOptions,
        OnBoardingForm,
        FormSection,
        JetInputError,
        Label,
        JetButton,
        JetActionMessage
    },
    computed: {
        riskCapacityCompleted() {
            let answeredQuestions = Object.values(this.form.risk_capacity).filter((current) => current && Number.isInteger(current.score));

            return answeredQuestions.length == Object.values(this.form.risk_capacity).length ? true : false;
        },
        riskCapacityStage() {
            let score = Object.values(this.form.risk_capacity).reduce((total, current) => {
                return total + (current && current.score ? current.score : 0);
            }, 0);

            switch (true) {
                case score >= 0 && score <= 9:
                    return 1;
                case score >= 10 && score <= 18:
                    return 2;
                case score >= 19 && score <= 27:
                    return 3;
                case score >= 28 && score <= 36:
                    return 4;
                case score >= 37 && score <= 45:
                    return 5;
            }
        },
        riskToleranceCompleted() {
            let answeredQuestions = Object.values(this.form.risk_tolerance).filter((current) => current && Number.isInteger(current.score));

            return answeredQuestions.length == Object.values(this.form.risk_tolerance).length ? true : false;
        },
        riskToleranceStage() {
            let score = Object.values(this.form.risk_tolerance).reduce((total, current) => {
                return total + (current && current.score ? current.score : 0);
            }, 0);

            switch (true) {
                case score >= 6 && score <= 8:
                    return 1;
                case score == 10:
                    return 2;
                case score >= 12 && score <= 14:
                    return 3;
                case score == 16:
                    return 4;
                case score >= 18 && score <= 20:
                    return 5;
            }
        },
        questionsAnswered() {
            return this.riskToleranceCompleted && this.riskCapacityCompleted;
        },
        totalRiskScore() {
            return this.riskCapacityStage < this.riskToleranceStage ? this.riskCapacityStage : this.riskToleranceStage;
        },
    },
    props: {
        riskCapacity: Object,
        riskTolerance: Object,
        experience: Object,
    },
    data() {
        return {
            results: {
                1: {
                    titleKey: 'Low risk',
                    descriptionKey: 'risk_result_1_description',
                },
                2: {
                    titleKey: 'Moderate risk',
                    descriptionKey: 'risk_result_2_description',
                },
                3: {
                    titleKey: 'Medium risk',
                    descriptionKey: 'risk_result_3_description',
                },
                4: {
                    titleKey: 'Elevated risk',
                    descriptionKey: 'risk_result_4_description',
                },
                5: {
                    titleKey: 'High risk',
                    descriptionKey: 'risk_result_5_description',
                },
            },
            form: this.$inertia.form({
                risk_capacity: {
                    question_1: this.riskCapacity ? this.riskCapacity.question_1 : null,
                    question_2: this.riskCapacity ? this.riskCapacity.question_2 : null,
                    question_3: this.riskCapacity ? this.riskCapacity.question_3 : null,
                    question_4: this.riskCapacity ? this.riskCapacity.question_4 : null,
                    question_5: this.riskCapacity ? this.riskCapacity.question_5 : null,
                    question_6: this.riskCapacity ? this.riskCapacity.question_6 : null,
                    question_7: this.riskCapacity ? this.riskCapacity.question_7 : null,
                },
                risk_tolerance: {
                    question_1: this.riskTolerance ? this.riskTolerance.question_1 : null,
                    question_2: this.riskTolerance ? this.riskTolerance.question_2 : null,
                    question_3: this.riskTolerance ? this.riskTolerance.question_3 : null,
                },
                experience: {
                    question_1: this.experience ? this.experience.question_1 : null,
                    question_2: this.experience ? this.experience.question_2 : null,
                    question_3: this.experience ? this.experience.question_3 : null,
                }
            }),
            questions: {
                risk_capacity: {
                    question_1: {
                        question: 'Which investment objective is most important to you?',
                        options: [
                            {name: "Regular income", score: 0},
                            {name: "Long-term capital preservation", score: 3},
                            {name: "Long-term profit generation", score: 6},
                        ],
                    },
                    question_2: {
                        question: 'Are your income greater than your expenses?',
                        options: [
                            {name: "No, I have no surplus", score: 3},
                            {name: "Yes, I can save assets", score: 6},
                        ]
                    },
                    question_3: {
                        question: 'Over what period can the invested capital be invested?',
                        options: [
                            {name: "0-3 years", score: 0},
                            {name: "4-6 years", score: 3},
                            {name: "7-10 years", score: 6},
                            {name: ">10 years", score: 9},
                        ]
                    },
                    question_4: {
                        question: 'What is your total net worth in CHF after deducting debts?',
                        options: [
                            {name: "0-1 million", score: 0},
                            {name: "1-5 million", score: 2},
                            {name: "5-10 million", score: 4},
                            {name: ">10 million", score: 6},
                        ]
                    },
                    question_5: {
                        question: 'What is the value of your bank-held assets in CHF excluding business interests and real estate investments?',
                        options: [
                            {name: "0-1 million", score: 0},
                            {name: "1-5 million", score: 2},
                            {name: "5-10 million", score: 4},
                            {name: ">10 million", score: 6},
                        ]
                    },
                    question_6: {
                        question: 'What is your annual total net income in CHF?',
                        options: [
                            {name: "0-100,000", score: 0},
                            {name: "100,000-250,000", score: 3},
                            {name: ">250,000", score: 6},
                        ]
                    },
                    question_7: {
                        question: 'Besides your income, do you rely on additional regular income from your assets?',
                        options: [
                            {name: "Yes", score: 0},
                            {name: "No", score: 6},
                        ]
                    }
                },
                risk_tolerance: {
                    question_1: {
                        question: 'risk_tolerance_q1',
                        options: [
                            {
                                name: "risk_tolerance_q1_opt1_name",
                                description: "risk_tolerance_q1_opt1_desc",
                                score: 2
                            },
                            {
                                name: "risk_tolerance_q1_opt2_name",
                                description: "risk_tolerance_q1_opt2_desc",
                                score: 4
                            },
                            {
                                name: "risk_tolerance_q1_opt3_name",
                                description: "risk_tolerance_q1_opt3_desc",
                                score: 6
                            },
                        ]
                    },
                    question_2: {
                        question: 'risk_tolerance_q2',
                        options: [
                            {name: "risk_tolerance_q2_opt1_name", description: "+/- 5%", score: 2},
                            {name: "risk_tolerance_q2_opt2_name", description: "+/- 12%", score: 4},
                            {name: "risk_tolerance_q2_opt3_name", description: "+/- 20%", score: 6},
                        ]
                    },
                    question_3: {
                        question: 'risk_tolerance_q3',
                        options: [
                            {
                                name: "risk_tolerance_q3_opt1",
                                description: null,
                                score: 2
                            },
                            {
                                name: "risk_tolerance_q3_opt2",
                                description: null,
                                score: 4
                            },
                            {
                                name: "risk_tolerance_q3_opt3",
                                description: null,
                                score: 6
                            },
                            {
                                name: "risk_tolerance_q3_opt4",
                                description: null,
                                score: 8
                            },
                        ]
                    }
                },
                experience: {
                    question_1: {
                        question: 'experience_q1',
                        options: [
                            {name: "experience_q1_opt1"},
                            {name: "experience_q1_opt2"},
                            {name: "experience_q1_opt3"},
                        ],
                    },
                    question_2: {
                        question: 'experience_q2',
                        options: [
                            {name: "experience_q2_opt1"},
                            {name: "experience_q2_opt2"},
                            {name: "experience_q2_opt3"},
                            {name: "experience_q2_opt4"},
                        ],
                    },
                    question_3: {
                        question: 'experience_q3',
                        options: [
                            {name: "experience_q3_opt1"},
                            {name: "experience_q3_opt2"},
                            {name: "experience_q3_opt3"},
                        ],
                    },
                },
            }
        };
    },
    methods: {
        submitForm() {
            this.form.post(route('onboarding.step2.store'), {
                preserveScroll: false,
                preserveState: true,
            })
        },
    }

});
</script>
